import { Button, Icons, LinkButton } from "@flash-tecnologia/hros-web-ui-v2";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "src/components/core-components/atoms/Box/box";
import { ProgressBarFullWidth } from "src/components/core-components/atoms/ProgressBarFullWidth/progress-bar-full-width";
import {
  StepMoment,
  Stepper,
  StepperStep,
} from "src/components/core-components/molecules/Stepper/stepper";
import { Focused } from "src/components/core-components/templates/Focused/focused";

import dispatchToast from "@/utils/dispatch-toast";
import { useNavigate, useParams } from "react-router-dom";

import {
  ManageMembersProvider,
  useManageMembersForm,
} from "./context/manage-members-context";
import {
  SetupSummaryProvider,
  useSetupSummary,
} from "./context/setup-summary-context";
import { useCreateMembers } from "./hooks/use-create-members";
import { InviteMembers, ManageMembers, SetupSummary } from "./steps";
import { SetupWorkflowSteps } from "./types";

import { trpc } from "@/api/client";
import { useGetSetup, useSession, useUpdateStepStatus } from "@/hooks";
import { useAddCompaniesToGroup } from "@/hooks/use-add-companies-to-group";
import { setEventTracking, setPageTracking } from "@/utils";
import { isNil } from "lodash-es";
import { WorkflowLoading } from "./components/WorkflowLoading/workflow-loading";
import {
  SETUP_STEP_TITLE_MAP,
  SETUP_WORKFLOW_STEPS_MAP,
  StepKeys,
  WORKFLOW_TRACKING_EVENTS_MAP,
  WORKFLOW_TRACKING_PAGES_MAP,
} from "./constants";
import {
  ManageCompaniesProvider,
  useManageCompaniesForm,
} from "./context/manage-companies-context";
import { ManageCompanies } from "./steps/ManageCompanies/manage-companies";

interface StepValidation {
  success: boolean;
  errorMessage?: string;
}

interface StepDefinition {
  id: SetupWorkflowSteps;
  label: string;
  canGoBack?: boolean;
  onNext?: () => Promise<StepValidation>;
  onComplete?: () => Promise<void>;
}

interface MainWorkflowContentProps {
  onWorkflowComplete?: () => void;
}

const MINIMUM_STEPS_TO_DISPLAY_STEPPER = 2;

const MainWorkflowContent = ({
  onWorkflowComplete,
}: MainWorkflowContentProps) => {
  const { setupId } = useParams();
  const navigate = useNavigate();
  const { companyId } = useSession();
  const { incrementCompaniesCreated, addEmployeeErrors } = useSetupSummary();

  const {
    members,
    invitationType: inviteType,
    invitationDate,
  } = useManageMembersForm();
  const { createMembers } = useCreateMembers({
    onCreateError: addEmployeeErrors,
  });
  const { newCompanies, mainCompany } = useManageCompaniesForm();
  const { updateStepStatus } = useUpdateStepStatus(companyId);
  const { setup, isLoading: isLoadingSetup } = useGetSetup(companyId);

  const [currentStepId, setCurrentStepId] = useState<SetupWorkflowSteps>();

  const [isLoading, setIsLoading] = useState(false);
  const [shouldShakeAddMemberButton, setShouldShakeAddMemberButton] =
    useState(false);

  useEffect(() => {
    if (setup) {
      const firstStep = setup.baseSteps?.find(({ status }) =>
        ["DOING", "PENDING"].includes(status)
      );

      const firstStepId =
        SETUP_WORKFLOW_STEPS_MAP[firstStep?.externalId as StepKeys];

      if (!isNil(firstStepId)) {
        setCurrentStepId(firstStepId);
      }
    }
  }, [setup]);

  const triggerErrorShake = useCallback(() => {
    setShouldShakeAddMemberButton(false);
    requestAnimationFrame(() => {
      setShouldShakeAddMemberButton(true);
    });
  }, []);

  const { mutateAsync: generateContract } = trpc.addCompanyTerm.useMutation({});
  const { addCompaniesToGroup } = useAddCompaniesToGroup({});

  const createCompanies = useCallback(async () => {
    if (!mainCompany) {
      dispatchToast({
        type: "error",
        content:
          "Ocorreu um erro ao obter dados da empresa principal. Tente novamente mais tarde.",
      });
      return;
    }

    let contracts = [];

    try {
      contracts = await Promise.all(
        newCompanies.map(async (company) => {
          const contract = await generateContract({
            mainCompany: {
              id: mainCompany.id,
              economicGroupId: mainCompany.economicGroupId,
              registrationNumber: mainCompany.registrationNumber,
              legalName: mainCompany.legalName,
              address: {
                zipCode: mainCompany.address.zipCode ?? "",
                street: mainCompany.address.street ?? "",
                number: mainCompany.address.number ?? "",
                complement: mainCompany.address.complement ?? "",
                district: mainCompany.address.district ?? "",
                state: mainCompany.address.state ?? "",
                city: mainCompany.address.city ?? "",
              },
            },
            companies: [
              {
                registrationNumber: company.registrationNumber,
                legalName: company.legalName,
                address: {
                  zipCode: company.address.zipCode ?? "",
                  street: company.address.street ?? "",
                  number: `${company.address.number}`,
                  complement: company.address.complement ?? "",
                  district: company.address.district ?? "",
                  state: company.address.state ?? "",
                  city: company.address.city ?? "",
                },
              },
            ],
          });

          if (!contract) {
            throw new Error();
          }

          return {
            registrationNumber: company.registrationNumber,
            ...contract,
          };
        })
      );
    } catch (error) {
      dispatchToast({
        type: "error",
        content:
          "Ocorreu um erro ao gerar os contratos. Tente novamente mais tarde.",
      });
      return;
    }

    if (contracts.length !== newCompanies.length) {
      dispatchToast({
        type: "error",
        content:
          "Ocorreu um erro no número de contratos gerados. Tente novamente mais tarde.",
      });
      return;
    }

    try {
      await Promise.all(
        contracts.map(async ({ registrationNumber, id }) => {
          const company = newCompanies.find(
            (company) => company.registrationNumber === registrationNumber
          );

          if (!company) {
            return;
          }

          await addCompaniesToGroup({
            companies: [
              {
                address: {
                  zipCode: company.address.zipCode ?? "",
                  street: company.address.street ?? "",
                  number: `${company.address.number}`,
                  complement: company.address.complement ?? "",
                  district: company.address.district ?? "",
                  state: company.address.state ?? "",
                  city: company.address.city ?? "",
                },
                legalName: company.legalName,
                name: company.name,
                registrationNumber: company.registrationNumber,
              },
            ],
            termId: id,
          });

          await updateStepStatus({
            setupId: setupId!,
            externalId: StepKeys.ADD_COMPANIES,
            status: "COMPLETED",
          });
        })
      );

      incrementCompaniesCreated(contracts.length);
    } catch (error) {
      dispatchToast({
        type: "error",
        content:
          "Ocorreu um erro ao adicionar as empresas do grupo. Tente novamente mais tarde.",
      });
      throw error;
    }
  }, [
    newCompanies,
    mainCompany,
    generateContract,
    addCompaniesToGroup,
    updateStepStatus,
    setupId,
    incrementCompaniesCreated,
  ]);

  const stepDefinitions = useMemo<StepDefinition[]>(
    () =>
      setup?.baseSteps.reduce<StepDefinition[]>((acc, step) => {
        const setupWorkflowStep = step.externalId as StepKeys;
        if (!setupWorkflowStep) return acc;

        if (["COMPLETED", "SKIPPED"].includes(step.status)) return acc;

        if (setupWorkflowStep === StepKeys.ADD_MEMBERS) {
          const addMembersStep: StepDefinition = {
            id: SetupWorkflowSteps.ADD_MEMBERS,
            label: SETUP_STEP_TITLE_MAP[SetupWorkflowSteps.ADD_MEMBERS],
            canGoBack: false,
            onNext: async () => {
              const hasMembers = members.length > 0;
              if (!hasMembers) triggerErrorShake();

              return {
                success: hasMembers,
                errorMessage: hasMembers
                  ? undefined
                  : "Adicione pelo menos um membro para continuar",
              };
            },
          };

          const inviteMembersStep = {
            id: SetupWorkflowSteps.INVITE_MEMBERS,
            label: SETUP_STEP_TITLE_MAP[SetupWorkflowSteps.INVITE_MEMBERS],
            canGoBack: true,
            onNext: async () => {
              if (
                !inviteType ||
                (inviteType === "SCHEDULED" && !invitationDate)
              ) {
                return {
                  success: false,
                  errorMessage: "Defina uma data para envio de convite",
                };
              }

              await createMembers(
                members.map((member) => ({ ...member, invitationDate }))
              );

              await updateStepStatus({
                setupId: setupId!,
                externalId: StepKeys.ADD_MEMBERS,
                status: "COMPLETED",
              });

              return {
                success: true,
              };
            },
          };
          return [...acc, addMembersStep, inviteMembersStep];
        }

        if (setupWorkflowStep === StepKeys.ADD_COMPANIES) {
          const addCompaniesStep = {
            id: SetupWorkflowSteps.ADD_COMPANIES,
            label: SETUP_STEP_TITLE_MAP[SetupWorkflowSteps.ADD_COMPANIES],
            canGoBack: false,
            onNext: async () => {
              if (!newCompanies.length) {
                await updateStepStatus({
                  setupId: setupId!,
                  externalId: StepKeys.ADD_COMPANIES,
                  status: "SKIPPED",
                });
              } else {
                await createCompanies();
              }

              return { success: true };
            },
          };
          return [...acc, addCompaniesStep];
        }

        return acc;
      }, []) ?? [],
    [
      createCompanies,
      createMembers,
      members,
      inviteType,
      invitationDate,
      setup,
      setupId,
      newCompanies,
      triggerErrorShake,
      updateStepStatus,
    ]
  );

  const currentStepIndex = stepDefinitions.findIndex(
    (step) => step.id === currentStepId
  );
  const currentStep = stepDefinitions[currentStepIndex];

  const handleNext = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await currentStep.onNext?.();
      if (!response?.success) {
        dispatchToast({ type: "error", content: response?.errorMessage });
        return;
      }

      const isLastStep = currentStepIndex === stepDefinitions.length - 1;
      if (isLastStep) {
        setCurrentStepId(SetupWorkflowSteps.SUMMARY);
      } else {
        const nextStep = stepDefinitions[currentStepIndex + 1];
        setCurrentStepId(nextStep.id);
      }
    } finally {
      setIsLoading(false);
      const trackingEvent = !isNil(currentStepId)
        ? WORKFLOW_TRACKING_EVENTS_MAP[currentStepId]
        : undefined;
      if (trackingEvent && "continue" in trackingEvent) {
        setEventTracking(trackingEvent.continue);
      }
    }
  }, [currentStep, currentStepIndex, stepDefinitions, currentStepId]);

  const handlePrevious = useCallback(() => {
    const trackingEvent = !isNil(currentStepId)
      ? WORKFLOW_TRACKING_EVENTS_MAP[currentStepId]
      : undefined;
    if (trackingEvent && "goBack" in trackingEvent) {
      setEventTracking(trackingEvent.goBack);
    }
    if (!currentStep.canGoBack) return;

    const previousStep = stepDefinitions[currentStepIndex - 1];
    setCurrentStepId(previousStep.id);
  }, [currentStep, currentStepIndex, stepDefinitions, currentStepId]);

  const handleContinueLaterClick = useCallback(() => {
    const trackingEvent = !isNil(currentStepId)
      ? WORKFLOW_TRACKING_EVENTS_MAP[currentStepId]
      : undefined;
    if (trackingEvent && "continueLater" in trackingEvent) {
      setEventTracking(trackingEvent.continueLater);
    }
    navigate("/home");
  }, [currentStepId, navigate]);

  const stepperProps = useMemo(() => {
    if (currentStepId === SetupWorkflowSteps.SUMMARY) {
      return null;
    }

    const availableSteps = setup?.baseSteps.reduce<{
      [stepperStepId: string]: StepperStep;
    }>((acc, step) => {
      const stepperStepId =
        SETUP_WORKFLOW_STEPS_MAP[step.externalId as StepKeys];

      if (isNil(stepperStepId) || acc[stepperStepId]) {
        return acc;
      }

      const stepTitle = SETUP_STEP_TITLE_MAP[stepperStepId];

      if (step.status === "COMPLETED") {
        acc[stepperStepId] = {
          id: `${stepperStepId}`,
          label: stepTitle,
          state: StepMoment.Completed,
        };
        return acc;
      }

      if (isNil(currentStepId)) {
        acc[stepperStepId] = {
          id: `${stepperStepId}`,
          label: stepTitle,
        };
        return acc;
      }

      acc[stepperStepId] = {
        id: `${stepperStepId}`,
        label: stepTitle,
        state: stepperStepId < currentStepId ? StepMoment.Completed : undefined,
      };

      return acc;
    }, {});

    const stepperSteps = availableSteps && Object.values(availableSteps);

    const isStepperVisible =
      stepperSteps && stepperSteps.length >= MINIMUM_STEPS_TO_DISPLAY_STEPPER;

    if (!isStepperVisible) {
      return null;
    }

    let activeStepId = stepperSteps[0].id;

    if (!isNil(currentStepId)) {
      activeStepId = `${currentStepId}`;
    } else {
      const firstPendingStep = stepperSteps.find((step) => !step.state);
      if (firstPendingStep) {
        activeStepId = firstPendingStep.id;
      }
    }

    const stepperCurrentStepIndex = stepperSteps.findIndex(
      (step) => step.id === `${activeStepId}`
    );

    const meta =
      stepperCurrentStepIndex !== -1
        ? `${stepperCurrentStepIndex + 1} de ${stepperSteps.length}`
        : "";

    return {
      activeStepId,
      steps: stepperSteps,
      meta,
    };
  }, [currentStepId, setup?.baseSteps]);

  const onFinish = useCallback(() => {
    if (onWorkflowComplete) {
      onWorkflowComplete();
    } else {
      navigate("/home");
    }
  }, [navigate, onWorkflowComplete]);

  const completionPercent = !stepDefinitions.length
    ? 0
    : ((currentStepIndex + 1) / stepDefinitions.length) * 100;

  useEffect(() => {
    !isNil(currentStepId) &&
      setPageTracking(WORKFLOW_TRACKING_PAGES_MAP.workflow[currentStepId]);
  }, [currentStepId]);

  const renderCurrentStep = () => {
    switch (currentStepId) {
      case SetupWorkflowSteps.ADD_MEMBERS:
        return (
          <ManageMembers
            shouldShakeAddMemberButton={shouldShakeAddMemberButton}
          />
        );
      case SetupWorkflowSteps.INVITE_MEMBERS:
        return <InviteMembers />;
      case SetupWorkflowSteps.ADD_COMPANIES:
        return <ManageCompanies />;
      case SetupWorkflowSteps.SUMMARY:
        return <SetupSummary />;
    }
  };

  if (isLoadingSetup) {
    return <WorkflowLoading />;
  }

  return (
    <Focused.Root fullScreen>
      <Focused.Header>
        <Focused.Title color="neutral.30">
          Configuração da plataforma
        </Focused.Title>
        <Box gap="xs">
          {stepperProps && (
            <Stepper
              steps={stepperProps.steps}
              activeStepId={`${stepperProps.activeStepId}`}
              meta={stepperProps.meta}
            />
          )}
        </Box>
      </Focused.Header>
      <ProgressBarFullWidth.Root>
        <ProgressBarFullWidth.ActiveBar percent={completionPercent} />
        <ProgressBarFullWidth.GuideLine />
      </ProgressBarFullWidth.Root>
      <Focused.Main>{renderCurrentStep()}</Focused.Main>

      <Focused.Footer>
        {currentStepId === SetupWorkflowSteps.SUMMARY ? (
          <Box justifyContent="center" $width={"100%"}>
            <Button
              variant="primary"
              size="medium"
              onClick={onFinish}
              loading={isLoading}
            >
              Fechar
            </Button>
          </Box>
        ) : (
          <>
            <LinkButton
              variant="neutral"
              size="medium"
              underlined={false}
              onClick={handleContinueLaterClick}
            >
              Continuar mais tarde
            </LinkButton>
            <Box gap="xs2">
              <Button
                variant="secondary"
                size="medium"
                disabled={!currentStep?.canGoBack || isLoading}
                onClick={handlePrevious}
              >
                <Icons name="IconArrowLeft" /> Voltar
              </Button>
              <Button
                variant="primary"
                size="medium"
                onClick={handleNext}
                loading={isLoading}
              >
                {isLoading
                  ? "Processando..."
                  : currentStepIndex === stepDefinitions.length - 1
                  ? "Concluir configuração"
                  : "Continuar"}{" "}
                <Icons name="IconArrowRight" />
              </Button>
            </Box>
          </>
        )}
      </Focused.Footer>
    </Focused.Root>
  );
};

export const MainWorkflow = ({
  onWorkflowComplete,
}: MainWorkflowContentProps) => (
  <SetupSummaryProvider>
    <ManageMembersProvider>
      <ManageCompaniesProvider>
        <MainWorkflowContent onWorkflowComplete={onWorkflowComplete} />
      </ManageCompaniesProvider>
    </ManageMembersProvider>
  </SetupSummaryProvider>
);
